import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAsyncData } from "../hooks/useAsyncData";
import { blogService } from "../services/blog.service";
import { IBlogCard } from "../interfaces/blog/iBlogCard.interface";
import { BlogQueryVM } from "../interfaces/blog/iBlogQuery.interface";
import { PageContainer } from "../components/PageContainer";
import { EmptyState } from "../components/EmptyState";
import { SkeletonGrid } from "../components/SkeletonGrid";
import { Seo } from "../components/Seo";
import {
  heroPanelClasses,
  pageHeadingLargeClasses,
  pageLeadClasses,
  subtleEyebrowClasses,
} from "../styles/pageClasses";
import {
  homePageClasses,
  blogPageClasses,
} from "../styles/pageSpecificClasses";
import { primaryBtnModernClasses, secondaryBtnModernClasses } from "../styles/uiClasses";
import "../styles/heroBackgrounds.css";

type BlogListResponse = {
  items: IBlogCard[];
  totalCount: number;
};

export default function Blog() {
  const [searchParams] = useSearchParams();
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 6;
  const q = searchParams.get("q") ?? "";

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQ(q), 220);
    return () => window.clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQ]);

  const { data, loading, error } = useAsyncData<BlogListResponse>(
    () => {
      const query: BlogQueryVM = {
        q: debouncedQ.trim() || undefined,
        sort: 1,
        page,
        pageSize,
      };
      return blogService.getAll(query);
    },
    [debouncedQ, page]
  );

  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <PageContainer maxWidth="7xl">
      <Seo
        title="Blog and Guides"
        description="Explore guides for summer jobs in Greece, seasonal work on the Greek islands, and practical advice before applying for tourism and hospitality roles."
        path="/blog"
      />

      <section className={heroPanelClasses}>
        <div className={homePageClasses.heroGrid}>
          <div className={homePageClasses.heroCopyCard}>
            <div>
              <span className={subtleEyebrowClasses}>Blog & Guides</span>
              <h1 className={`mt-4 max-w-3xl ${pageHeadingLargeClasses}`}>
                Travel-led stories, practical guides, and seasonal advice for Greece.
              </h1>
              <p className={`max-w-2xl ${pageLeadClasses}`}>
                Explore practical guides for summer jobs in Greece, seasonal work on the Greek islands, and what to know before applying for hospitality, tourism, bar, restaurant, and hotel roles.
              </p>
            </div>
            <div className={homePageClasses.mobileCtaWrap}>
              <a href="#blog-posts" className={`${homePageClasses.mobileCta} ${primaryBtnModernClasses}`}>
                Explore Articles
              </a>
            </div>
          </div>

          <div className={blogPageClasses.heroSide}>
            <div className={blogPageClasses.heroImage} />
            <div className={blogPageClasses.heroNoteGrid}>
              <div className={blogPageClasses.heroNoteWarm}>
                <p className={blogPageClasses.heroNoteTitle}>Editorial Style</p>
                <p className={blogPageClasses.heroNoteCopy}>Designed to feel closer to a magazine spread than a utility dashboard.</p>
              </div>
              <div className={blogPageClasses.heroNoteCool}>
                <p className={blogPageClasses.heroNoteTitle}>Useful Content</p>
                <p className={blogPageClasses.heroNoteCopy}>Advice, inspiration, and destination context for seasonal life in Greece.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="blog-posts" className={blogPageClasses.sectionSpacing}>
        {error && <div className={homePageClasses.error}>{error}</div>}

        {loading ? (
          <SkeletonGrid />
        ) : items.length === 0 ? (
          <EmptyState
            title="No blog posts found"
            subtitle="Try a different search or check back when more guides are published."
          />
        ) : (
          <>
            <div className={blogPageClasses.cardGrid}>
              {items.map((item) => (
                <article key={item.id} className={blogPageClasses.card}>
                  {item.coverImageUrl ? (
                    <img
                      src={item.coverImageUrl}
                      alt={item.coverImageAlt ?? item.title}
                      className={blogPageClasses.cardImage}
                    />
                  ) : (
                    <div className={blogPageClasses.cardImageFallback} />
                  )}

                  <div className={blogPageClasses.cardBody}>
                    <div className={blogPageClasses.cardMeta}>
                      {item.categoryName ? <span>{item.categoryName}</span> : <span>Guide</span>}
                      <span>{formatDate(item.publishedAt ?? item.createdDate)}</span>
                    </div>

                    <h2 className={blogPageClasses.cardTitle}>{item.title}</h2>

                    {item.summary && <p className={blogPageClasses.cardSummary}>{item.summary}</p>}

                    <div className={blogPageClasses.cardFooter}>
                      <span className={blogPageClasses.cardReadTime}>
                        {item.readTimeMinutes ? `${item.readTimeMinutes} min read` : "Article"}
                      </span>
                      <Link to={`/blog/${item.slug}`} className={secondaryBtnModernClasses}>
                        Read Post
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {totalPages > 1 && (
              <div className={blogPageClasses.pagination}>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={secondaryBtnModernClasses}
                >
                  Previous
                </button>
                <span className={blogPageClasses.paginationText}>
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={primaryBtnModernClasses}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </PageContainer>
  );
}

function formatDate(value?: string | null) {
  if (!value) return "Recently added";
  return new Date(value).toLocaleDateString();
}
